// /admin/mensajes — operator console: an account manager's inbox of client
// threads ("Mensajes"). Left: the AM's assigned client tenants, most-recent
// first, with a last-message preview + unread badge. Right: the selected thread,
// reusing the same ConciergeThread + composer the client surface uses.
//
// Data via the admin-concierge-data seam. Opening a thread marks that client's
// messages read on the server, so we invalidate the thread list (and the shell's
// nav badge shares that query). Sends are optimistic then refetched; failures
// roll back with toast.error.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  type AdminConciergeThreadData,
  type AdminConciergeThreadSummary,
  type ConciergeMessage,
  getAdminConciergeThread,
  getAdminConciergeThreads,
  sendAdminConciergeMessage,
} from './admin-concierge-data.js'
import { cn, PageHeader, Spinner, toast } from '../../design-system/index.js'
import { conciergeInitials, formatConciergeTime } from '../concierge/concierge-data.js'
import { ConciergeThread } from '../concierge/ConciergeThread.js'

const THREADS_KEY = ['admin-concierge', 'threads']

function ThreadRow({
  thread,
  active,
  onSelect,
}: {
  thread: AdminConciergeThreadSummary
  active: boolean
  onSelect: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex w-full gap-3 border-b border-line px-3.5 py-3 text-left transition-colors last:border-b-0',
        active ? 'bg-hover' : 'hover:bg-hover',
        'focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-blue',
      )}
    >
      <span className="grid size-9 flex-none place-items-center rounded-full bg-field text-[11px] font-bold text-text-2">
        {conciergeInitials(thread.tenantName)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-[12.5px] text-text',
              thread.unread > 0 ? 'font-bold' : 'font-semibold',
            )}
          >
            {thread.tenantName}
          </span>
          {thread.lastAt && (
            <span className="flex-none text-[10px] text-text-3">
              {formatConciergeTime(thread.lastAt)}
            </span>
          )}
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[11.5px] text-text-2">
            {thread.lastBody ?? 'Sin mensajes todavía'}
          </span>
          {thread.unread > 0 && (
            <span className="flex-none rounded-pill bg-brand-blue px-[7px] py-px text-[10px] font-bold tabular-nums text-white">
              {thread.unread}
            </span>
          )}
        </span>
      </span>
    </button>
  )
}

export function AdminConciergePage(): JSX.Element {
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTenantId = searchParams.get('t')

  const { data: threads, isLoading } = useQuery({
    queryKey: THREADS_KEY,
    queryFn: getAdminConciergeThreads,
    // Keep the inbox list (previews + unread badges) live while it's open.
    refetchInterval: 5000,
  })

  const threadKey = ['admin-concierge', 'thread', activeTenantId]
  const { data: thread, isLoading: threadLoading } = useQuery({
    queryKey: threadKey,
    queryFn: () => getAdminConciergeThread(activeTenantId!),
    enabled: activeTenantId !== null,
    // Poll the open thread so client replies + read-status appear without a reload.
    refetchInterval: 5000,
  })

  // Opening a thread cleared the client's unread server-side: refresh the list.
  const threadLoaded = thread !== undefined
  useEffect(() => {
    if (threadLoaded) void qc.invalidateQueries({ queryKey: THREADS_KEY })
  }, [threadLoaded, activeTenantId, qc])

  const sendMutation = useMutation({
    mutationFn: (body: string) => sendAdminConciergeMessage(activeTenantId!, body),
    onError: () => {
      toast.error('No se pudo enviar', { description: 'Inténtalo de nuevo.' })
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['admin-concierge', 'thread', activeTenantId] })
      void qc.invalidateQueries({ queryKey: THREADS_KEY })
    },
  })

  function select(tenantId: string): void {
    setSearchParams({ t: tenantId })
  }

  function onSend(body: string): void {
    if (activeTenantId === null) return
    const optimistic: ConciergeMessage = {
      id: `tmp-${Date.now()}`,
      side: 'manager',
      authorName: 'Tú',
      body,
      createdAt: new Date().toISOString(),
      // Not yet read by the client: shows "Enviado" until a poll reports it read.
      read: false,
    }
    qc.setQueryData<AdminConciergeThreadData>(
      ['admin-concierge', 'thread', activeTenantId],
      (cur) => (cur ? { ...cur, messages: [...cur.messages, optimistic] } : cur),
    )
    sendMutation.mutate(body)
  }

  const activeThread = threads?.find((t) => t.tenantId === activeTenantId) ?? null

  return (
    <div data-testid="admin-concierge" className="flex h-full flex-col">
      <PageHeader title="Mensajes" subtitle="Tus conversaciones con los clientes que gestionas." />
      <div className="mt-4 flex min-h-0 flex-1 overflow-hidden rounded-xl border border-line">
        {/* Thread list */}
        <nav
          aria-label="Clientes"
          className={cn(
            'min-h-0 w-full shrink-0 flex-col overflow-y-auto border-r border-line bg-surface-2 md:flex md:w-[300px]',
            activeTenantId ? 'hidden' : 'flex',
          )}
        >
          {isLoading ? (
            <div className="p-6">
              <Spinner label="Cargando conversaciones…" />
            </div>
          ) : !threads || threads.length === 0 ? (
            <div className="grid h-full place-items-center px-6 text-center text-[12.5px] leading-relaxed text-text-3">
              Aún no tienes conversaciones con clientes.
            </div>
          ) : (
            threads.map((th) => (
              <ThreadRow
                key={th.tenantId}
                thread={th}
                active={th.tenantId === activeTenantId}
                onSelect={() => select(th.tenantId)}
              />
            ))
          )}
        </nav>

        {/* Active thread */}
        <main
          className={cn(
            'min-h-0 min-w-0 flex-1 flex-col bg-surface md:flex',
            activeTenantId ? 'flex' : 'hidden',
          )}
        >
          {activeTenantId === null ? (
            <div className="grid h-full place-items-center text-[12.5px] text-text-3">
              Selecciona un cliente para ver la conversación.
            </div>
          ) : threadLoading || !thread ? (
            <div className="p-6">
              <Spinner label="Cargando conversación…" />
            </div>
          ) : (
            <ConciergeThread
              messages={thread.messages}
              mySide="manager"
              headerName={activeThread?.tenantName ?? 'Cliente'}
              headerSubtitle="Cliente que gestionas"
              {...(activeTenantId ? { headerHref: `/admin/tenants/${activeTenantId}` } : {})}
              headerInitials={conciergeInitials(activeThread?.tenantName ?? 'Cliente')}
              emptyText="Aún no hay mensajes. Escríbele a tu cliente cuando quieras."
              onSend={onSend}
              sending={sendMutation.isPending}
              placeholder="Escribe un mensaje…"
              onBack={() => setSearchParams({})}
            />
          )}
        </main>
      </div>
    </div>
  )
}
